/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { AnimationBuilder } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
    AfterContentInit,
    AfterViewInit,
    Component,
    ContentChildren,
    DestroyRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { TimerProgressBarComponent } from '@frontend/sports/common/ui/progress-bar';
import { DsArrow, DsArrowSize } from '@frontend/ui/arrow';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { filter } from 'rxjs';

import { IndicatorsComponent } from './indicators.component';
import { BaseSlideBehavior } from './slide-behavior/base-slide-behavior';
import { OneSlideBehavior } from './slide-behavior/one-slide-behavior';
import { ThreeSlideBehavior } from './slide-behavior/three-slide-behavior';
import { SlideComponent } from './slide.component';
import { ArrowDirection, NavigationMode, SlideshowMode } from './slideshow.model';

@Component({
    selector: 'ms-slideshow',
    templateUrl: './slideshow.component.html',
    imports: [IndicatorsComponent, TimerProgressBarComponent, CommonModule, IconCustomComponent, DsArrow],
    styleUrl: 'slideshow.component.scss',
})
export class SlideshowComponent implements AfterViewInit, AfterContentInit, OnChanges {
    @Input() delay = 2000;
    @Input() circular = true;
    @Input() auto = false;
    @Input() size: DsArrowSize = 'medium';
    @Input() mode = SlideshowMode.OneSlide;
    @Input() slideIndex = 0;
    @Input() pageindicators = false;
    @Input() showSingleIndicator = false;
    @Input() realtotalslidecount = 0;
    @Output() slideChanged = new EventEmitter<number>();
    @Input() isAutoSwipeLeftOnProgressBar = false;
    @Input() navigationMode: NavigationMode = NavigationMode.Swipe;
    @Input() indicatorArrow = false;
    @Input() arrowPosition?: number;
    @Output() navigation = new EventEmitter<{ navigationMode: NavigationMode; direction: string }>();
    @Input() showProgressBar = false;
    @Input() overlayArea: { position: string; width: string };
    @Input() disableSwipe = false;
    @Output() swipeNotApplicable = new EventEmitter<void>();
    @ViewChild(TimerProgressBarComponent)
    private progressBar: TimerProgressBarComponent;

    readonly NavigationMode = NavigationMode;
    readonly SlideshowMode = SlideshowMode;
    readonly ArrowDirection = ArrowDirection;

    private _slides: SlideComponent[] = [];
    pauseProgressBarOnHover: boolean;

    @ContentChildren(SlideComponent) slideComponents: QueryList<SlideComponent>;

    private slideshowBehavior: BaseSlideBehavior;
    private cancelSlideshowInterval?: () => void;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private animationBuilder: AnimationBuilder,
        private timerService: TimerService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        const slideIndexChange = changes['slideIndex'];
        if (slideIndexChange && !slideIndexChange.isFirstChange()) {
            this.slideToIndex(slideIndexChange.currentValue);
        }

        const mode = changes['mode'];
        if (mode && !mode.isFirstChange()) {
            this.initializeSlideshow();
        }
    }

    ngAfterViewInit(): void {
        this.initializeSlideshow();
    }

    ngAfterContentInit(): void {
        this._slides = this.slideComponents.toArray();
        this.slideComponents.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this._slides = this.slideComponents.toArray();
            this.initializeSlideshow();
        });
    }

    get slides(): SlideComponent[] {
        return this._slides;
    }

    get currentSlideIndex(): number {
        if (!this.slideshowBehavior) {
            return 0;
        }

        return this.slideshowBehavior.currentSlideIndex;
    }

    get showIndicators(): boolean {
        const indicatorLimit = this.showSingleIndicator ? 0 : 1;

        return this.pageindicators && this.slides && this.slides.length > indicatorLimit;
    }

    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    swipeLeft(isManualSwipe: boolean = true): void {
        if (this.disableSwipe && isManualSwipe) {
            return;
        }
        this.slideLeft();
        this.tryStartCarousel();
    }

    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    swipeRight(isManualSwipe: boolean = true): void {
        if (this.disableSwipe && isManualSwipe) {
            return;
        }
        this.slideRight();
        this.tryStartCarousel();
    }

    slideTo(slideTowards: number | ArrowDirection): void {
        if (slideTowards === ArrowDirection.Left) {
            this.swipeRight();
        } else if (slideTowards === ArrowDirection.Right) {
            this.swipeLeft();
        } else {
            this.slideToIndex(slideTowards);
            this.tryStartCarousel();
        }
    }

    addSlide(slide: SlideComponent): void {
        this._slides.push(slide);

        if (this._slides.length > this.visibleSlideCount) {
            slide.moveOutside();
        }
    }

    private slideToIndex(index: number): void {
        this.slideshowBehavior.slideTo(index);
    }

    private get visibleSlideCount(): number {
        return this.mode === SlideshowMode.OneSlide ? 1 : 3;
    }

    private tryStartCarousel(): void {
        if (!this.auto) {
            return;
        }
        if (this.showProgressBar) {
            return;
        }

        if (this.cancelSlideshowInterval) {
            this.cancelSlideshowInterval();
        }

        this.cancelSlideshowInterval = this.timerService.setInterval(() => {
            this.swipeLeft(false);
        }, this.delay);
    }

    private initializeSlideshow(): void {
        this.slideshowBehavior = this.createSlideBehavior();
        this.slideshowBehavior.slideChanged$
            .pipe(
                filter((s) => !!s),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((selectedSlide) => {
                const selectedIndex = this._slides.indexOf(selectedSlide!);
                this.slideChanged.emit(selectedIndex);
                if (this.showProgressBar) {
                    this.progressBar.resetProgressBar();
                }
            });

        this.tryStartCarousel();
    }

    private createSlideBehavior(): BaseSlideBehavior {
        return this.mode === SlideshowMode.OneSlide
            ? new OneSlideBehavior(this._slides, this.animationBuilder, this.slideIndex)
            : new ThreeSlideBehavior(this._slides, this.animationBuilder, this.slideIndex);
    }

    private slideLeft(): void {
        if (!this.slideshowBehavior.canSlideLeft) {
            this.swipeNotApplicable.emit();
        }

        if (this.slideshowBehavior && (this.circular || this.slideshowBehavior.canSlideLeft)) {
            this.slideshowBehavior.slideLeft();
        }
    }

    private slideRight(): void {
        if (!this.slideshowBehavior.canSlideRight) {
            this.swipeNotApplicable.emit();
        }

        if (this.slideshowBehavior && (this.circular || this.slideshowBehavior.canSlideRight)) {
            this.slideshowBehavior.slideRight();
        }
    }

    arrowClicked(clickValue: string): void {
        if (clickValue === ArrowDirection.Left) {
            this.swipeLeft();
        } else if (clickValue === ArrowDirection.Right) {
            this.swipeRight();
        }
        this.navigation.emit({ navigationMode: NavigationMode.Arrows, direction: clickValue });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onArrowHover(event: any, value: boolean): void {
        event?.stopPropagation();
        this.pauseProgressBarOnHover = value;
    }
}
